import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Boxes, Plus, QrCode, Download, Printer, Power, Loader2, Trash2, RefreshCw, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";

export default function Cabins() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [qr, setQr] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBranch, setBulkBranch] = useState("");
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const canDelete = ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN"].includes(user.role);

  const load = () => api.get("/cabins").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm({ status: "active", branch_id: branches[0]?.id }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };

  const save = async () => {
    if (!form.branch_id || !form.member_company) { toast.error("Branch and Member Company are required"); return; }
    setSaving(true);
    try {
      const payload = { branch_id: form.branch_id, member_company: form.member_company, member_name: form.member_name, member_email: form.member_email || null, member_phone: form.member_phone, status: form.status, number: form.number ? Number(form.number) : null };
      if (editing) { await api.put(`/cabins/${editing.id}`, payload); toast.success("Cabin updated"); }
      else { const { data } = await api.post("/cabins", payload); toast.success(`Cabin created: ${data.cabin_code}`); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const toggle = async (c) => { try { await api.patch(`/cabins/${c.id}/status`); load(); } catch (e) { toast.error(apiError(e)); } };
  const openQr = async (c) => {
    try { const { data } = await api.get(`/cabins/${c.id}/qr`); setQr({ ...data, cabin: c }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const regenQr = async () => { try { await api.post(`/cabins/${qr.cabin.id}/regenerate-qr`); toast.success("QR regenerated"); openQr(qr.cabin); load(); } catch (e) { toast.error(apiError(e)); } };
  const toggleQr = async () => { try { const { data } = await api.patch(`/cabins/${qr.cabin.id}/qr`); toast.success(data.qr_enabled ? "QR enabled" : "QR disabled"); openQr({ ...qr.cabin, qr_enabled: data.qr_enabled }); load(); } catch (e) { toast.error(apiError(e)); } };
  const printQr = () => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${qr.cabin_code}</title></head><body style="text-align:center;font-family:Arial;padding:40px"><h2 style="color:#16305B">${qr.cabin_code}</h2><p style="color:#64748b">${qr.cabin.member_company} · ${qr.cabin.branch}</p><img src="${qr.qr}" style="width:320px"/><p style="color:#94a3b8;font-size:12px;margin-top:20px">Scan to access your Cabin Member Portal · Suvera Flow</p></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };
  const doDelete = async () => { try { await api.delete(`/cabins/${delTarget.id}`); toast.success("Cabin deleted"); setDelTarget(null); load(); } catch (e) { toast.error(apiError(e)); } };

  const openBulk = () => { setBulkBranch(branches[0]?.id || ""); setBulkFile(null); setBulkResult(null); setBulkOpen(true); };
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/cabins/bulk-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "cabin-bulk-template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const doBulkUpload = async () => {
    if (!bulkBranch) { toast.error("Select a branch"); return; }
    if (!bulkFile) { toast.error("Choose an Excel file"); return; }
    setUploading(true); setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("branch_id", bulkBranch);
      fd.append("file", bulkFile);
      const { data } = await api.post("/cabins/bulk-upload", fd);
      setBulkResult(data);
      if (data.created_count > 0) toast.success(`${data.created_count} cabin(s) created`);
      if (data.created_count === 0 && data.error_count > 0) toast.error("No cabins created — see errors below");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "-";
  if (!list) return <Loader />;

  return (
    <div>
      <PageHeader title="Cabins" subtitle="Cabin members, auto-generated codes and portal QR codes."
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={openBulk} data-testid="bulk-upload-btn"><Upload size={16} className="mr-1.5" /> Bulk Upload</Button>
          <Button onClick={openNew} data-testid="add-cabin-btn" className="bg-navy-900 hover:bg-navy-800"><Plus size={16} className="mr-1.5" /> New Cabin</Button>
        </div>} />

      {list.length === 0 ? <EmptyState icon={Boxes} title="No cabins yet" subtitle="Create a cabin to generate its code and portal QR." /> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden sf-fade-up">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Cabin Code</TableHead><TableHead>Member Company</TableHead><TableHead>Member</TableHead>
              <TableHead>Branch</TableHead><TableHead>Portal</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} data-testid={`cabin-row-${c.cabin_code}`}>
                  <TableCell className="font-mono font-semibold text-navy-900">{c.cabin_code}</TableCell>
                  <TableCell className="text-navy-800">{c.member_company}</TableCell>
                  <TableCell className="text-slate-500">{c.member_name || "-"}<div className="text-xs text-slate-400">{c.member_email}</div></TableCell>
                  <TableCell className="text-slate-500">{branchName(c.branch_id)}</TableCell>
                  <TableCell><span className={`text-xs font-semibold px-2 py-1 rounded-md ${c.has_password ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{c.has_password ? "Active" : "Not set"}</span></TableCell>
                  <TableCell><StatusBadge status={c.status} /></TableCell>
                  <TableCell className="text-right space-x-2 whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openQr(c)} data-testid={`cabin-qr-${c.cabin_code}`} title="Portal QR"><QrCode size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-cabin-${c.cabin_code}`}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => toggle(c)} title="Toggle status"><Power size={14} /></Button>
                    {canDelete && <Button size="sm" variant="outline" onClick={() => setDelTarget(c)} data-testid={`delete-cabin-${c.cabin_code}`} className="border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Cabin" : "New Cabin"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div><Label>Branch *</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="cabin-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Member Company *</Label><Input data-testid="cabin-member-company" className="mt-1 h-10" value={form.member_company || ""} onChange={(e) => setForm({ ...form, member_company: e.target.value })} placeholder="e.g. Suvera Technology" /></div>
            {editing && <div><Label>Cabin Code</Label><Input className="mt-1 h-10 font-mono bg-slate-50" value={editing.cabin_code} disabled /></div>}
            {!editing && <div><Label>Cabin Number <span className="text-slate-400 font-normal">(optional — auto if blank)</span></Label><Input type="number" data-testid="cabin-number" className="mt-1 h-10" value={form.number || ""} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="e.g. 1" /></div>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Member Name</Label><Input data-testid="cabin-member-name" className="mt-1 h-10" value={form.member_name || ""} onChange={(e) => setForm({ ...form, member_name: e.target.value })} /></div>
              <div><Label>Member Phone</Label><Input data-testid="cabin-member-phone" className="mt-1 h-10" value={form.member_phone || ""} onChange={(e) => setForm({ ...form, member_phone: e.target.value })} /></div>
            </div>
            <div><Label>Member Email <span className="text-slate-400 font-normal">(used for portal login & password reset)</span></Label><Input type="email" data-testid="cabin-member-email" className="mt-1 h-10" value={form.member_email || ""} onChange={(e) => setForm({ ...form, member_email: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-cabin-btn" className="bg-navy-900">{saving ? <Loader2 className="animate-spin" size={16} /> : (editing ? "Save" : "Create Cabin")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cabin Portal QR</DialogTitle></DialogHeader>
          {qr && (
            <div className="flex flex-col items-center text-center">
              <p className="font-mono font-bold text-navy-900 text-lg">{qr.cabin_code}</p>
              <p className="text-sm text-slate-500 mb-3">{qr.cabin.member_company} · {qr.cabin.branch}</p>
              <img src={qr.qr} alt="Cabin QR" className="w-56 h-56" data-testid="cabin-qr-image" />
              <p className="text-xs text-slate-400 mt-2 break-all">{qr.url}</p>
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                <a href={qr.qr} download={`${qr.cabin_code}.png`}><Button size="sm" variant="outline"><Download size={14} className="mr-1.5" /> Download</Button></a>
                <Button size="sm" variant="outline" onClick={printQr}><Printer size={14} className="mr-1.5" /> Print</Button>
                <Button size="sm" variant="outline" onClick={regenQr}><RefreshCw size={14} className="mr-1.5" /> Regenerate</Button>
                <Button size="sm" variant="outline" onClick={toggleQr} className={qr.qr_enabled ? "" : "text-rose-600 border-rose-200"}>{qr.qr_enabled ? "Disable QR" : "Enable QR"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Bulk Upload Cabins</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
              <span className="text-sm text-slate-600">Step 1 — Download the Excel template</span>
              <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn"><FileSpreadsheet size={14} className="mr-1.5" /> Template</Button>
            </div>
            <div><Label>Branch *</Label>
              <Select value={bulkBranch} onValueChange={setBulkBranch}>
                <SelectTrigger className="mt-1" data-testid="bulk-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Excel file (.xlsx) *</Label>
              <Input type="file" accept=".xlsx" data-testid="bulk-file-input" className="mt-1" onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }} />
              <p className="text-xs text-slate-400 mt-1">Columns: Member Company*, Member Name, Member Email, Member Phone, Cabin Number (optional). Codes &amp; QR are generated automatically.</p>
            </div>
            {bulkResult && (
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-auto" data-testid="bulk-result">
                <div className="flex items-center gap-2 px-3 py-2 text-sm text-emerald-700"><CheckCircle2 size={15} /> {bulkResult.created_count} cabin(s) created</div>
                {bulkResult.error_count > 0 && <div className="flex items-center gap-2 px-3 py-2 text-sm text-rose-600"><AlertTriangle size={15} /> {bulkResult.error_count} row(s) skipped</div>}
                {(bulkResult.errors || []).map((er, i) => (
                  <div key={i} className="px-3 py-1.5 text-xs text-slate-500" data-testid={`bulk-error-${i}`}>Row {er.row}: {er.message}</div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Close</Button>
            <Button onClick={doBulkUpload} disabled={uploading} data-testid="do-bulk-upload-btn" className="bg-navy-900">{uploading ? <Loader2 className="animate-spin" size={16} /> : "Upload & Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Cabin</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Permanently delete <span className="font-mono font-medium text-navy-900">{delTarget?.cabin_code}</span>? The member will lose portal access. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button onClick={doDelete} data-testid="confirm-delete-cabin-btn" className="bg-rose-600 hover:bg-rose-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
